import { useState, useEffect } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Table, Tag, Badge, Modal, Input, Select, DatePicker, Spin } from "antd";
import { Phone, Play, Pause, Plus } from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import {
    AI_CALLER_CAMPAIGNS_URL,
    AI_CALLER_CAMPAIGN_DETAIL_URL,
    AI_CALLER_CAMPAIGN_START_URL,
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import dayjs from "dayjs";

// ── helpers ───────────────────────────────────────────────────────────────────

const STATUS_BADGE = {
    draft: { status: "default", text: "Draft" },
    scheduled: { status: "processing", text: "Scheduled", color: "blue" },
    running: { status: "processing", text: "Running", color: "green" },
    completed: { status: "success", text: "Completed" },
    paused: { status: "warning", text: "Paused", color: "orange" },
    cancelled: { status: "error", text: "Cancelled" },
};

const STATUS_TAG_COLORS = {
    draft: "default",
    scheduled: "blue",
    running: "green",
    completed: "green",
    paused: "orange",
    cancelled: "red",
};

const LEAD_STAGE_OPTIONS = [
    { value: "New Lead", label: "New Lead" },
    { value: "Contacted", label: "Contacted" },
    { value: "Qualified", label: "Qualified" },
    { value: "Proposal", label: "Proposal" },
    { value: "Negotiation", label: "Negotiation" },
    { value: "Demo Fixed", label: "Demo Fixed" },
    { value: "Demo Done", label: "Demo Done" },
    { value: "On Hold", label: "On Hold" },
    { value: "Unanswered", label: "Unanswered" },
];

const LEAD_SOURCE_OPTIONS = [
    { value: "website", label: "Website" },
    { value: "referral", label: "Referral" },
    { value: "google_ads", label: "Google Ads" },
    { value: "facebook", label: "Facebook" },
    { value: "instagram", label: "Instagram" },
    { value: "linkedin", label: "LinkedIn" },
    { value: "cold_call", label: "Cold Call" },
    { value: "other", label: "Other" },
];

const EMPTY_FORM = {
    name: "",
    audience_filter: {
        lead_stage: [],
        lead_source: [],
        city: "",
    },
    schedule_at: null,
};

// ── main component ────────────────────────────────────────────────────────────

export default function AICallerCampaigns() {
    const { fetch, create, update } = useApiMethods();

    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);

    // new campaign modal
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [saving, setSaving] = useState(false);

    const loadCampaigns = async () => {
        setLoading(true);
        const res = await fetch(AI_CALLER_CAMPAIGNS_URL);
        if (res?.status === "success") {
            setCampaigns(res.data || []);
        }
        setLoading(false);
    };

    useEffect(() => { loadCampaigns(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCreate = async () => {
        if (!form.name.trim()) {
            ToastMsg("error", "Campaign name is required");
            return;
        }
        setSaving(true);
        const payload = {
            name: form.name.trim(),
            audience_filter: {
                lead_stage: form.audience_filter.lead_stage,
                lead_source: form.audience_filter.lead_source,
                city: form.audience_filter.city.trim(),
            },
            schedule_at: form.schedule_at ? form.schedule_at.toISOString() : null,
        };
        const res = await create(AI_CALLER_CAMPAIGNS_URL, payload);
        setSaving(false);
        if (res?.status === "success") {
            ToastMsg("success", "Campaign created");
            setModalOpen(false);
            setForm({ ...EMPTY_FORM });
            loadCampaigns();
        } else {
            ToastMsg("error", res?.message || "Failed to create campaign");
        }
    };

    const handleStart = async (id) => {
        const res = await create(AI_CALLER_CAMPAIGN_START_URL(id), {});
        if (res?.status === "success") {
            ToastMsg("success", "Campaign started");
            loadCampaigns();
        } else {
            ToastMsg("error", res?.message || "Failed to start campaign");
        }
    };

    const handlePause = async (id) => {
        const res = await update(AI_CALLER_CAMPAIGN_DETAIL_URL(id), { status: "paused" });
        if (res?.status === "success") {
            ToastMsg("success", "Campaign paused");
            loadCampaigns();
        } else {
            ToastMsg("error", res?.message || "Failed to pause campaign");
        }
    };

    // ── table columns ─────────────────────────────────────────────────────────

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (text) => <span className="font-medium text-gray-800 dark:text-gray-100">{text}</span>,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={STATUS_TAG_COLORS[status] || "default"}>
                    {status === "running" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse mr-1" />}
                    {(status || "").replace("_", " ").toUpperCase()}
                </Tag>
            ),
        },
        {
            title: "Total Leads",
            dataIndex: "total_leads",
            key: "total_leads",
            render: (val) => val ?? "—",
        },
        {
            title: "Called",
            dataIndex: "called_count",
            key: "called_count",
            render: (val) => val ?? "—",
        },
        {
            title: "Qualified",
            dataIndex: "qualified_count",
            key: "qualified_count",
            render: (val) => val ?? "—",
        },
        {
            title: "Demos Booked",
            dataIndex: "demos_booked",
            key: "demos_booked",
            render: (val) => val ?? "—",
        },
        {
            title: "Created",
            dataIndex: "created_at",
            key: "created_at",
            render: (val) => val ? dayjs(val).format("DD MMM YYYY") : "—",
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <div className="flex items-center gap-2">
                    {record.status === "draft" && (
                        <button
                            onClick={(e) => { e.stopPropagation(); handleStart(record.id); }}
                            className="text-gray-400 hover:text-green-500"
                            title="Start Campaign"
                        >
                            <Play className="h-4 w-4" />
                        </button>
                    )}
                    {record.status === "running" && (
                        <button
                            onClick={(e) => { e.stopPropagation(); handlePause(record.id); }}
                            className="text-gray-400 hover:text-orange-500"
                            title="Pause Campaign"
                        >
                            <Pause className="h-4 w-4" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center justify-between mb-4 p-2">
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                        <Phone className="h-5 w-5 text-teal-600" /> AI Caller Campaigns
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage bulk AI calling campaigns</p>
                </div>
                <Button
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-2 text-sm"
                >
                    <Plus className="h-4 w-4" /> New Campaign
                </Button>
            </div>

            {/* Table */}
            <div className="px-2 mb-2">
                <Table
                    columns={columns}
                    dataSource={campaigns}
                    rowKey="id"
                    loading={loading}
                    size="small"
                    locale={{ emptyText: "No campaigns yet" }}
                    pagination={{ pageSize: 20, showSizeChanger: false }}
                />
            </div>

            {/* New Campaign Modal */}
            <Modal
                title="New Campaign"
                open={modalOpen}
                onCancel={() => { setModalOpen(false); setForm({ ...EMPTY_FORM }); }}
                onOk={handleCreate}
                confirmLoading={saving}
                okText="Create"
                width={560}
            >
                <div className="space-y-4 py-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Campaign Name
                        </label>
                        <Input
                            placeholder="e.g. Q1 Lead Outreach"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Lead Stage Filter
                        </label>
                        <Select
                            mode="multiple"
                            placeholder="Select lead stages"
                            value={form.audience_filter.lead_stage}
                            onChange={(val) => setForm({
                                ...form,
                                audience_filter: { ...form.audience_filter, lead_stage: val },
                            })}
                            options={LEAD_STAGE_OPTIONS}
                            className="w-full"
                            allowClear
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Lead Source Filter
                        </label>
                        <Select
                            mode="multiple"
                            placeholder="Select lead sources"
                            value={form.audience_filter.lead_source}
                            onChange={(val) => setForm({
                                ...form,
                                audience_filter: { ...form.audience_filter, lead_source: val },
                            })}
                            options={LEAD_SOURCE_OPTIONS}
                            className="w-full"
                            allowClear
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            City
                        </label>
                        <Input
                            placeholder="e.g. Chennai"
                            value={form.audience_filter.city}
                            onChange={(e) => setForm({
                                ...form,
                                audience_filter: { ...form.audience_filter, city: e.target.value },
                            })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Schedule
                        </label>
                        <DatePicker
                            showTime
                            value={form.schedule_at}
                            onChange={(val) => setForm({ ...form, schedule_at: val })}
                            className="w-full"
                            placeholder="Select date and time"
                        />
                    </div>
                </div>
            </Modal>
        </ContainerCard>
    );
}
